import React from 'react';
import {motion} from 'framer-motion';

const containerVariants = {
  hidden: {opacity: 0},
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      ease: 'easeInOut',
    },
  },
};

const itemVariants = {
  hidden: {opacity: 0, y: 20},
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 12,
    },
  },
};

const sectionFadeIn = {
  initial: {opacity: 0, y: 30},
  whileInView: {opacity: 1, y: 0},
  transition: {duration: 0.7, ease: 'easeInOut'},
  viewport: {once: true, amount: 0.2},
};

export default function ProblemSection() {
  const problems = [
    {
      emoji: '💔',
      title: 'Everyone Knows You, But Nobody Sees You',
      desc: '"Amma", "Akka", "Aunty" — but enakku oru identity irukkaa? You want to be known for who YOU are, not just your role.',
    },
    {
      emoji: '😔',
      title: 'Society Says "Just a Housewife"',
      desc: 'You\'ve managed a home, solved problems, and handled emotions every day. But society still says "just a housewife." You deserve more respect.',
    },
    {
      emoji: '⏰',
      title: 'Time Is Slipping, But Dreams Are Still There',
      desc: 'Morning family. Afternoon home. Evening responsibilities. But deep inside, you still want to do something for yourself. Is it too late?',
    },
    {
      emoji: '💰',
      title: 'Want Income But Don\'t Know Where to Start',
      desc: 'You want to earn, but traditional jobs don\'t fit your life. "Yenna panna mudiyum? Who will hire me?" — these doubts hold you back.',
    },
    {
      emoji: '😟',
      title: 'Tired of Asking for Money',
      desc: 'Asking husband or family for money — even for small things — hurts your self-respect. You want financial independence without guilt.',
    },
    {
      emoji: '🤔',
      title: 'Family Says "Why Do You Need This?"',
      desc: 'When you talk about doing something for yourself, family questions it. "Veetu vela paathaale pothum" nu solluranga. But you want more.',
    },
    {
      emoji: '📚',
      title: 'You Help Everyone — But Never Get Paid',
      desc: 'Friends call you for advice. Family trusts your words. People feel better after talking to you. But you never thought this could be a career.',
    },
    {
      emoji: '🎯',
      title: 'Want Flexibility, Not a Fixed Job',
      desc: 'You can\'t work 9-5. You need flexibility around family time. But flexible income opportunities feel impossible to find.',
    },
    {
      emoji: '💪',
      title: 'Ready to Build, But Need Confidence',
      desc: 'You have the skills, experience, and strength. But confidence irukaadhu. "Ennaala mudiyumaa?" — this fear stops you from starting.',
    },
  ];

  return (
    <motion.section
      className="problem-section"
      initial="initial"
      whileInView="whileInView"
      transition={sectionFadeIn.transition}
      viewport={sectionFadeIn.viewport}
    >
      <div className="container">
        <motion.div className="section-content">
          <motion.h2 {...sectionFadeIn}>
            💫 Dear Women: Do These Feelings Resonate With You?
          </motion.h2>

          <motion.p
            className="problem-intro"
            {...sectionFadeIn}
            transition={{delay: 0.1, ...sectionFadeIn.transition}}
          >
            If you're feeling any of this, you're not alone. Thousands of women face these struggles — not because they lack capability, but because they don't know how to turn their life experience into a respected career with flexibility and purpose.
          </motion.p>

          <motion.div
            className="problem-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{once: true, amount: 0.2}}
          >
            {problems.map((problem) => (
              <motion.div
                key={problem.title}
                className="problem-card"
                variants={itemVariants}
                whileHover={{y: -8, transition: {duration: 0.2}}}
              >
                <h3>
                  {problem.emoji} {problem.title}
                </h3>
                <p>{problem.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </motion.section>
  );
}